import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Time } from '@apmg/titan';
import { globals } from '../../config/globals';
import { collectionLinkData } from '../../utils/utils';
import AudioPlayButton from '../../components/AudioPlayButton/AudioPlayButton';
import Content from '../../components/Content/Content';
import Metatags from '../../components/Metatags/Metatags';
import ShareSocialButtons from '../../components/ShareSocialButtons/ShareSocialButtons';
import { Image } from '@apmg/mimas';
import { fishForSocialMediaImage } from '../../components/Metatags/MetaTagHelpers';
import { audioDownloadPrefix } from '../../utils/utils';
import Transcript from '../../components/Transcript/Transcript';
import DonateAsk from '../../components/DonateAsk/DonateAsk';
import MnLegacy from '../../components/MnLegacy/MnLegacy';
import '/public/css/components/apmGallerySlideshow.css';

const Episode = ({ data: { episode, donateAsk }, wordcount }) => {
  const img = fishForSocialMediaImage(episode);
  const rssUrl = episode?.collections?.find(
    (item) => item.rssUrl?.length
  )?.rssUrl;
  let authors;
  const AppContext = React.createContext(JSON.parse(donateAsk.json));

  const DonateAskContext = () => {
    const context = useContext(AppContext);
    return <DonateAsk context={context} />;
  };

  if (episode.contributors) {
    authors = episode.contributors.map((contributor) => {
      let thisString = `${
        contributor.profile?.firstName ? contributor.profile.firstName : ''
      } ${contributor.profile?.lastName ? contributor.profile.lastName : ''}`;
      return {
        // prettier-ignore
        title: `${thisString}`,
        href: '/people/[...slug]',
        as: `/people/${contributor.profile?.canonicalSlug}`,
      };
    });
  }

  return (
    <>
      <Metatags
        title={episode.title}
        seoHeadline={episode.seoHeadline}
        fullSlug={`episode/${episode.canonicalSlug}`}
        description={episode.descriptionText}
        image={img?.url}
        imageHeight={img?.height}
        imageWidth={img?.width}
        imageAlt={episode?.primaryVisuals?.social?.shortCaption}
        topic={episode.primaryCollection?.title}
        contentType="article"
        publishDate={episode.publishDate}
        publishedTags={episode.publishedTags}
        updatedAt={episode.updatedAt}
        originalSourceUrl={episode.originalSourceUrl}
        rssUrl={rssUrl}
        wordcount={wordcount}
      />
      <Content
        title={episode.title}
        subtitle={episode.subtitle}
        id={episode.id}
        authors={authors}
        body={episode.body}
        donateAsk={DonateAskContext}
        resourceType="episode"
        contentUrl={`episode/${episode.canonicalSlug}`}
        collections={episode.collections}
        primaryCollection={episode.primaryCollection}
        shareButtons={
          <ShareSocialButtons
            contentUrl={`episode/${episode.canonicalSlug}`}
            title={episode.title}
            contentType={episode.resourceType}
            resourceType={episode.resourceType}
          />
        }
        audioPlayButton={
          episode?.primaryAudio?.encodings?.[0]?.playFilePath && (
            <AudioPlayButton
              audioSource={audioDownloadPrefix(
                episode.primaryAudio.encodings[0].playFilePath
              )}
              audioTitle={episode.primaryAudio.title}
              audioThumbnailSrc={
                episode?.primaryVisuals?.lead?.aspect_ratios?.square
                  ?.instances[0]?.url
              }
              audioThumbnailAlt={
                episode?.primaryVisuals?.lead?.shortCaption || episode.title
              }
              label="Listen"
              elementClass="playButton-primary"
              showTitle={true}
            />
          )
        }
        image={
          episode.primaryVisuals?.lead && (
            <Image
              key={episode.primaryVisuals.lead.fallback}
              image={episode.primaryVisuals.lead}
              sizes={globals.sizes.primaryVisuals}
              alt={episode.primaryVisuals.lead.shortCaption}
            />
          )
        }
        imageCaption={episode.primaryVisuals?.lead?.longCaption}
        imageCredit={episode.primaryVisuals?.lead?.credit?.name}
        imageCreditHref={episode.primaryVisuals?.lead?.credit?.url}
        video={episode.primaryVisuals?.video}
        publishDate={<Time dateTime={episode.publishDate} />}
        updatedPublishDate={
          episode.updatedPublishDate && (
            <Time dateTime={episode.updatedPublishDate} />
          )
        }
        publishedTags={episode.publishedTags}
        embeddedAssets={episode.embeddedAssets}
        tag={collectionLinkData(episode.primaryCollection)}
        elementClass="episode"
        useDonate={true}
        transcript={<Transcript {...episode.primaryAudio} />}
      />
      <MnLegacy collections={episode.collections} />
    </>
  );
};

Episode.propTypes = {
  wordcount: PropTypes.number,
  data: PropTypes.shape({
    alertConfig: PropTypes.object,
    donateAsk: PropTypes.object,
    episode: PropTypes.shape({
      canonicalSlug: PropTypes.string,
      title: PropTypes.string,
      seoHeadline: PropTypes.string,
      subtitle: PropTypes.string,
      id: PropTypes.string,
      authors: PropTypes.arrayOf(
        PropTypes.shape({
          title: PropTypes.string,
          href: PropTypes.string,
        })
      ),
      body: PropTypes.string,
      wordcount: PropTypes.number,
      resourceType: PropTypes.string,
      contributors: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
      description: PropTypes.string,
      descriptionText: PropTypes.string,
      image: PropTypes.element,
      imageCaption: PropTypes.string,
      imageCredit: PropTypes.string,
      imageCreditHref: PropTypes.string,
      primaryVisuals: PropTypes.any,
      primaryCollection: PropTypes.any,
      collections: PropTypes.array,
      primaryAudio: PropTypes.any,
      publishDate: PropTypes.string,
      publishedTags: PropTypes.array,
      updatedPublishDate: PropTypes.string,
      updatedAt: PropTypes.string,
      embeddedAssets: PropTypes.object,
      originalSourceUrl: PropTypes.string,
      tag: PropTypes.shape({
        tagName: PropTypes.string,
        to: PropTypes.string,
      }),
    }),
  }),
};

export default Episode;
